
import { Component, Vue, Watch, Prop, Inject } from "vue-property-decorator";
import { State, Getter, Action, Mutation, namespace } from "vuex-class";
import getScript from "@/services/insertScript.service";
import { axiosInstance } from "@/axios.instance";
import { FeaturesList, UserPermissions } from "@/store/types";
import {
	getEnhancedMapping,
	IEnhancedMapping,
	IMap,
	IMarker,
	ILayer,
	ICircle,
	ILatLng,
	ILatLngBounds,
	convertStringToLatLng,
	addMeterToCoord,
	stringToBounds,
	union,
	extend,
	MapSearch
} from "@sureview/v2-mapping";
import { get, cloneDeep } from "lodash";
import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { EventDetails } from "@/store/site-monitor/types";

import { config } from "@vue/test-utils";
import { GPSService, GeoCodeResponse } from "@/services/gps.service";
import { months } from "moment";
import { ExternalMapLayer, ExternalMapLayerParameter } from "../store/external-map-layers/types";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ViewsEventBus from "@/services/views/viewsEventBus";
import { Marker, NewMarker } from "@/types/EnhancedMap";
import { MapSetupItem } from "@/store/map-setup/types";
import ExternalMapLayerPropertiesSetup from "@/components/ExternalMapLayerPropertiesSetup.vue";
import { Group } from "@/store/eventqueue/types";


class LocationBounds {
	public location: ILatLng;
	public viewport: {
		northeast: ILatLng;
		southwest: ILatLng;
	};
}

class MapBounds {
	public north: number = 0;
	public south: number = 0;
	public east: number = 0;
	public west: number = 0;
	public elevation: number = 1;
}

class MapLayerData {
	public mapLayerID: number = 0;
	public minElevation: number | null = null;
	public maxElevation: number | null = null;

	public title: string = "";
	public ne: string = "";
	public sw: string = "";

	public rotation: number = 0;
}

class MapItemData {
	public mapLayerItemID: number = 0;
	public mapLayerItemTypeID: number = 0;

	public minElevation: number | null = null;
	public maxElevation: number | null = null;

	public objectID: number | null = null;
	public extraValue: string = "";

	public title: string = "";
	public regionPath: string | null = null;
	public latLong: string | null = null;

	public brokenObjectLink: Boolean;
}

class MapItemTypeData {
	public mapLayerItemTypeID: number = 0;
	public title: string = "";
	public visible: boolean = true;
	public any: boolean = false;
	public hideByDefault: boolean = false;
}

class MapItemIcon {}

class LayerMap {
	[s: number]: ILayer;
}
class ItemMap {
	[s: number]: IMarker;
}
class ItemObjectMap {
	[s: number]: IMarker[];
}
class ItemTypeObjectMap {
	[s: number]: ItemObjectMap;
}

class LayerTypeMap {
	[s: number]: boolean;
}

const MapLayers = namespace("mapLayers");
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const ExternalMapLayerStore = namespace("externalMapLayers");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"map-search": MapSearch,
		"external-properties": ExternalMapLayerPropertiesSetup
	}
})
export default class EnhancedMap extends Vue {
	@Prop({ default: () => 19 })
	public zoomLevel: number;

	@Prop({ default: () => true })
	public showCircle: boolean;

	@Prop({ default: () => true })
	public showFilterControl: boolean;

	@Prop({ default: () => false })
	public filterMapItemsByDeviceId: boolean;

	@Prop({ default: () => [] })
	public deviceIdsToFilterBy: number[];

	@Prop({ default: () => -1 })
	public mapItemTypeIdToFilterBy: number;

	// Flag to specify if toggle map feature flag enabled, is it enabled for this instance of the re-usable component.
	@Prop({ type: Boolean, default: false }) showMapToggle: boolean;
	@Prop({ type: Boolean, default: false }) alwayShowMap: boolean;

	@Prop({ type: Boolean, default: false }) showMapSearch: boolean;

	@Prop({ default: null }) group: Group;

	@Inject("viewsEventBus") private eventBus: ViewsEventBus;

	public $refs: any = {
		mappingContainer: HTMLElement
	};

	@Getter("getMapKey") private mapKey: string;

	public elevation: number = 1;
	public displayElevation: string = "";

	public layersAdded = new LayerMap();
	public itemsAdded = new ItemMap();
	public markersByTypeAndObject = new ItemTypeObjectMap();

	public alarmsAdded = [];
	public eventIcons = {};

	public activeItems = new Array<IMarker>();
	public activeAlarmIDs = new Set<number>();
	public activeItemIDs = new Set<number>();

	public adHocAlarmMarkers = new ItemMap();
	public pendingAdHocMarkers: any[] = [];
	public mapCenter: ILatLng | null = null;

	private newMarker: NewMarker = null;
	private markers: any[] = [];

	public enhancedMapping: IEnhancedMapping | null = null;
	public map: IMap | null = null;

	public clickCircleEventID: number = 0;
	public clickCircle: ICircle | null = null;

	public layersVisible = false;

	public elevationList: any[] = [];

	public noMapData: boolean = true;
	public mapType: string = "";
	@MapLayers.Getter("getMapLayersItemTypes") public mapItemTypes: MapItemTypeData[];

	// Getter and setter for toggling the map.
	@SiteMonitor.Action setHideMapFlag: any;
	@SiteMonitor.Getter("getHideMapFlag") hideMapFlag: boolean;

	@MapLayers.Action public loadMapLayers: any;
	@MapLayers.Action public loadMapLayersItemTypes: any;
	@MapLayers.Action public loadAreaMapLayersBounds: any;
	@MapLayers.Getter("getMapItemIcons") public mapIcons!: any[];
	@MapLayers.Getter("getBrokenMapItemIcons") public brokenMapIcons!: any[];
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@SiteMonitor.Getter("getEventDetails") public EventDetails: EventDetails;
	@SiteMonitor.Getter("getActiveMapItemsRequired")
	public activeMapItemsRequired: any;
	@SiteMonitor.Getter("getActiveMapItems") public activeMapItems: any;
	@SiteMonitor.State public AdhocLocationEventRecords!: any[];
	@SiteMonitor.Getter("getEventLocationLastSet")
	public eventLocationLastSet: number;
	@SiteMonitor.Getter("getActiveResponseIDs") public activeResponseIDs: any;
	@SiteMonitor.Mutation public setActiveMapItems: any;
	@SiteMonitor.Mutation public setActiveMapItemsRequired: any;
	@SiteMonitor.Mutation public setMapCircleCenter: any;

	@SMCameras.Mutation public setAwaitingCamera: any;
	@SMCameras.Mutation clearDeviceControllerCameras: any;

	@Getter public getUseImperialMeasurements: any;
	@Getter public getMapType: any;
	@Getter public getHideLabels: any;
	@Getter private getFeature: (featureName: string[]) => boolean;
	@Mutation public setMapType: any;
	@MapLayers.Mutation public setMapLayersItemType: any;
	@Getter("getCustomMapViewTypes") public customMapViewTypes;

	/* External map layers functions */
	@ExternalMapLayerStore.Getter("getExternalMapLayers") externalLayersList!: ExternalMapLayer[];
	@ExternalMapLayerStore.Action loadExternalMapLayers: () => Promise<void>;
	@ExternalMapLayerStore.Action updateExternalMapLayer: (externalMapLayer: ExternalMapLayer) => Promise<void>;

	private created() {
		if (this.eventBus) {
			this.eventBus.$on("set-new-marker", (newMarker: NewMarker) => this.setNewIconOnMapLayer(newMarker));

			this.eventBus.$on("set-marker", marker => this.setIconOnMapLayer(marker));
		}
	}

	/**
	 * Retrieves the feature flag setting for whether or not we are displaying broken map items.
	 */
	private get showBrokenItemsFeatureEnabled(): boolean {
		return this.getFeature(["MapSetup", "ShowBrokenMapItems"]);
	}

	private get filterMapItemsByDeviceIds(): boolean {
		return this.filterMapItemsByDeviceId && this.deviceIdsToFilterBy.length > 0;
	}

	private get filterMapItemsByType(): boolean {
		return this.mapItemTypeIdToFilterBy > -1;
	}

	public get showMap(): boolean {
		return this.alwayShowMap || !this.noMapData;
	}

	public get eventLocation() {
		if (this.EventDetails == null) return "";

		return this.EventDetails.latLong;
	}

	public get allowSpotlightScaling() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "SpotlightScaling"]);
	}

	public get externalMapLayersEnabled(): boolean {
		return get(this.featuresList, ["ExternalMapLayers"]);
	}

	public get showInactiveAlarmsOnMap(): boolean {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "ShowInactiveAlarms"]);
	}

	public get canGoUp() {
		return this.elevationList.some(elevation => elevation.elevationValue > this.elevation);
	}

	public get canGoDown() {
		const index = this.elevationList.findIndex(x => x.elevationValue == this.elevation);
		if (index == 0 || index == -1) {
			return false;
		}

		return this.elevationList.some(elevation => elevation.elevationValue < this.elevation);
	}

	public get nearbyCameraRadius() {
		if (
			this.EventDetails == null ||
			this.EventDetails.cameraNearbyRadius == null ||
			this.EventDetails.cameraNearbyRadius == undefined ||
			this.EventDetails.cameraNearbyRadius < 1
		) {
			return 30;
		}
		return this.EventDetails.cameraNearbyRadius;
	}

	public get staticMapIcon(): boolean {
		if (
			this.EventDetails == null ||
			this.EventDetails.staticMapIcon == null ||
			this.EventDetails.staticMapIcon == undefined
		) {
			return false;
		}
		return this.EventDetails.staticMapIcon;
	}

	public get eventElevations() {
		var details = this.EventDetails;

		if (details == null) {
			return {
				minElevation: null,
				maxElevation: null
			};
		}

		return {
			minElevation: details.minElevation,
			maxElevation: details.maxElevation
		};
	}

	// Feature flag checker for toggle maps feature.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleSiteMonitor"]) && this.showMapToggle;
	}

	private async decodeLocation(location: string): Promise<LocationBounds> {
		if (/^-?[\d.]+[NS]? -?[\d.]+[WE]?($|,)/.test(location)) {
			return {
				location: convertStringToLatLng(location),
				viewport: null
			};
		} else {
			let geoCoder = new GPSService();
			let response = await geoCoder.GeoCodeLookup(location, this.mapKey);

			if (response == null || response.results.length == 0) return null;

			let addressGeometry = response.results[0].geometry;

			if (addressGeometry.viewport) {
				return {
					location: {
						lat: addressGeometry.location.lat,
						lng: addressGeometry.location.lng
					},
					viewport: {
						northeast: addressGeometry.viewport.northeast,
						southwest: addressGeometry.viewport.southwest
					}
				};
			} else {
				return {
					location: {
						lat: addressGeometry.location.lat,
						lng: addressGeometry.location.lng
					},
					viewport: null
				};
			}
		}
	}

	private get isFirstTwoEnabled(): boolean {
		return get(this.featuresList, ["ExternalMapLayers", "FirstTwoIntegration"]);
	}

	@Watch("eventLocationLastSet")
	public async onEventLocationChanged(value: string, oldValue: string) {
		if (this.map != null && this.EventDetails != null) {
			let elevationBounds = this.eventElevations;

			if (this.EventDetails.alarmElevation) {
				this.elevation = this.EventDetails.alarmElevation;
			} else if (elevationBounds.minElevation != null && this.elevation <= elevationBounds.minElevation) {
				this.elevation = elevationBounds.minElevation;
			} else if (elevationBounds.maxElevation != null && this.elevation <= elevationBounds.maxElevation) {
				this.elevation = elevationBounds.maxElevation;
			} else {
				this.elevation = elevationBounds.minElevation;
			}

			if (this.eventLocation == "") {
				this.noMapData = true;
			} else {
				let gpsLocation = await this.decodeLocation(this.eventLocation);

				if (gpsLocation) {
					this.mapCenter = gpsLocation.location;

					if (gpsLocation.viewport) {
						gpsLocation;
						this.map.mapBounds = {
							north: gpsLocation.viewport.northeast.lat,
							east: gpsLocation.viewport.northeast.lng,
							south: gpsLocation.viewport.southwest.lat,
							west: gpsLocation.viewport.southwest.lng
						};
					} else {
						this.map.center = this.mapCenter;
						this.map.zoom = this.zoomLevel;
					}

					this.noMapData = false;
				} else {
					this.noMapData = true;
				}
			}
		}
	}

	public get adhocLocationEventRecords() {
		return this.AdhocLocationEventRecords;
	}

	@Watch("adhocLocationEventRecords")
	public onAdhocLocationEventRecordsChange(records: any[], oldRecords: any[]) {
		if (this.map != null) {
			records.forEach(record => {
				if (this.adHocAlarmMarkers[record.eventRecordID] == null) {
					this.createAdHocMarker(record);
				}
			});
		}
	}

	@Watch("activeMapItemsRequired")
	public onActiveMapItemsRequiredChange(required: boolean, oldRequired: boolean) {
		if (required) {
			this.setActiveMapItems(this.activeMapItems);
			this.setActiveMapItemsRequired(false);
		}
	}

	@Watch("group", { deep: true })
	public async onGroupChanged(newGroup: Group) {
		let locationBounds = await this.calculateLocationBounds(this.group);
		
		if (locationBounds?.viewport) {
			const center = new google.maps.LatLngBounds(locationBounds?.viewport.southwest, locationBounds?.viewport.northeast).getCenter();
			this.map.map.setCenter(center);
		}
		else if (locationBounds?.location) {
			this.map.map.setCenter(new google.maps.LatLng(locationBounds.location.lat, locationBounds.location.lng));
		}
	}

	public async mounted() {
		await this.loadMapLayersItemTypes();
		await this.init();

		let brokenItemType: MapItemTypeData = {
			mapLayerItemTypeID: -1,
			title: "Broken items",
			visible: false,
			hideByDefault: true,
			any: true
		}

		// if we can't find the Broken Item Type, add it.
		if (this.showBrokenItemsFeatureEnabled && this.mapItemTypes.find( item => item.mapLayerItemTypeID == -1) === undefined)
		{
			this.setMapLayersItemType({ id: brokenItemType.mapLayerItemTypeID, data: brokenItemType });
		}

		if (this.EventDetails) {
			this.onEventLocationChanged(null, null);
		}
	}

	@Watch("elevationList", { deep: true })
	private updateDisplayElevation() {
		if (this.displayElevation == "" && this.elevationList.length > 0) {
			this.displayElevation = this.elevationList[0].name;
		}
	}

	public async beforeDestroy() {
		if (this.enhancedMapping) {
			await this.enhancedMapping.unloadExternalMapLayers(this.externalLayersList);
		}

		this.markers.forEach((_, i, arr) => {
			arr[i].marker.setMap(null);
		});

		this.markers = [];

		if (this.map) {
			this.map.reset();
		}

		this.clearMap();

		for (var eventrecordId in this.adHocAlarmMarkers) {
			this.adHocAlarmMarkers[eventrecordId].remove();
		}

		if (this.clickCircle) {
			this.clickCircle.remove();
			this.clickCircle = undefined;
		}
	}

	private toggleMarkersVisibilitty(visibilityLayer: boolean, mapLayerItemTypeID: number) {
		this.markers.forEach((eventMarker, index, arr) => {
			if (eventMarker.mapLayerItemTypeID == mapLayerItemTypeID) {
				arr[index].marker.setVisible(visibilityLayer);
			}
		});
	}

	private pointOfInterestLocation: ILatLng = null;
	@Watch("pointOfInterestLocation")
	private async updatePointOfInterest(newValue, oldValue): Promise<void> {
		// When changing the POI we need to re render the map to update any dynamic layers that query based on the POI.
		if (newValue && newValue != oldValue) {
			this.enhancedMapping.setPointOfInterest(newValue);
			await this.enhancedMapping.loadExternalMapLayers(this.externalLayersList, this.map.map);
		}
	}

	public async init() {
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);
		let pos: LocationBounds = {
			location: { lat: 0, lng: 0 },
			viewport: null
		};

		if (this.group === null) {
			if (this.eventLocation != null && this.eventLocation != "") {
				pos = await this.decodeLocation(this.eventLocation);

				let elevationBounds = this.eventElevations;

				if (this.EventDetails.alarmElevation) {
					this.elevation = this.EventDetails.alarmElevation;
				} else if (elevationBounds.minElevation != null && this.elevation <= elevationBounds.minElevation) {
					this.elevation = elevationBounds.minElevation;
				} else if (elevationBounds.maxElevation != null && this.elevation <= elevationBounds.maxElevation) {
					this.elevation = elevationBounds.maxElevation;
				} else {
					this.elevation = elevationBounds.minElevation;
				}

				if (pos) {
					this.noMapData = false;
				} else {
					this.noMapData = true;
					pos = {
						location: { lat: 0, lng: 0 },
						viewport: null
					};
				}
			} else {
				this.noMapData = true;
			}
		} else {
			this.noMapData = true;

			pos = await this.calculateLocationBounds(this.group);
		}

		this.mapCenter = pos.location;
		this.map = this.enhancedMapping.createMap(
			this.$refs.mappingContainer,
			pos.location,
			0,
			false,
			this.getMapType,
			getMapStyle(this.getMapType, this.getHideLabels),
			this.customMapViewTypes
		);

		this.$emit("map-created", this.map);

		if (pos.viewport) {
			const sw = pos.viewport.southwest;
			const ne = pos.viewport.northeast;

			this.map.mapBounds = {
				north: ne.lat,
				east: ne.lng,
				south: sw.lat,
				west: sw.lng
			};
		}

		this.mapType = this.getMapType;

		// setup even to handle a clist on the map. This is used to plot a new map item
		this.map!.registerEvent("onClick", this.setMarkerOnMap);

		this.map!.registerEvent("onMapTypeChanged", (mapType: string) => {
			this.mapType = mapType;
			this.setMapType(mapType);

			this.map!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		this.map!.registerEvent("onViewportChangedDelayed", () => {
			if (this.map!.currentViewState == "narrow") {
				this.loadInBounds(this.map!.mapBounds, true);
			} else {
				this.clearMap();
			}
		});

		this.map!.registerEvent("onDisplayTypeChanged", (data: any) => {
			var layersVisible = data.viewState === "narrow";

			for (var layerId in this.layersAdded) {
				this.layersAdded[layerId].visible = layersVisible;
			}

			for (var layerItemId in this.itemsAdded) {
				this.itemsAdded[layerItemId].visible = layersVisible;
			}
		});

		this.AdhocLocationEventRecords.forEach((record: any) => {
			this.createAdHocMarker(record);
		});

		if (this.externalMapLayersEnabled) {
			// Setting POI will trigger a loading of map layers
			// Call is done this way to avoid circular logic
			this.loadExternalMapLayers();
			this.updatePointOfInterest(pos.location, undefined);
		}
		// commented as it errors out
		//this.eventBus.$emit("set-map-ready", true);
	}

	private async calculateLocationBounds(group: Group): Promise<LocationBounds> {
		let pos: LocationBounds = {
			location: { lat: 0, lng: 0 },
			viewport: null
		};

		const bounds = await this.loadAreaMapLayersBounds(group.groupID);
		if (bounds && bounds !== "") {
			pos = {
				location: {
					lat: bounds.north,
					lng: bounds.east
				},
				viewport: {
					northeast: {
						lat: bounds.north,
						lng: bounds.east
					},
					southwest: {
						lat: bounds.south,
						lng: bounds.west
					}
				}
			};

			this.noMapData = false;
		} else if (this.group.address) {
			pos = await this.decodeLocation(this.group.address);
			if (pos) {
				this.noMapData = false;
			} else {
				pos = {
					location: { lat: 0, lng: 0 },
					viewport: null
				};
			}
		}

		return pos;
	}

	private getLatLngPolarity(latLng: string) {
		var regex = /([1-8]?[1-9]|[1-9])\.{1}\d{1,16}([A-Z])/g;

		var result = regex.exec(latLng);
		if (typeof result !== "undefined" && result !== null && result.length === 3) {
			return result[2];
		}

		return "";
	}

	private loadInBounds(mapBounds: any, layersVisible: boolean) {
		if (this.map) {
			// set the static icon setting
			// NOTE : dont move this code further up the chain, if this.EventDetails is null during Mount() -> Init() then on first load of site monitor no cameras will be loaded
			this.map!.staticMapIcon = this.staticMapIcon;
		}

		if (this.layersVisible != layersVisible) {
			for (var eventrecordId in this.adHocAlarmMarkers) {
				this.adHocAlarmMarkers[eventrecordId].visible = layersVisible;
			}
		}
		this.layersVisible = layersVisible;

		let bounds = {
			north: mapBounds.north,
			south: mapBounds.south,
			east: mapBounds.east,
			west: mapBounds.west
		} as MapBounds;

		if (this.elevation) bounds.elevation = this.elevation;

		this.loadMapLayers(bounds).then((response: any) => {
			var mapData = response.data;
			const elevations = mapData.elevations.slice(); // make a deep copy,
			// preventing mutation state outside mutation handlers.
			// Proper way would be to spread this logic accross the
			// `action -> service -> mutation -> getter` chain

			this.elevationList = elevations.sort(function(a, b) {
				return a.elevationValue - b.elevationValue;
			});

			if (this.elevation) {
				let currentElevation = elevations.find(
					(elevationFloor: any) => elevationFloor.elevationValue == this.elevation
				);
				if (currentElevation != null) {
					this.displayElevation = currentElevation.name;
				}
			} else if (elevations.length > 0) {
				let currentElevation = this.elevationList[0];
				if (currentElevation != null) {
					this.elevation = currentElevation.elevationValue;
					this.displayElevation = currentElevation.name;
				}
			}

			if (mapData.layers) {
				this.displayLayers(mapData.layers as MapLayerData[]);
			}
			if (mapData.items) {
				this.displayItems(mapData.items as MapItemData[]);
			}

			if (this.showCircle) {
				if (
					this.clickCircle == undefined ||
					(this.EventDetails && this.clickCircleEventID != this.EventDetails.eventID)
				) {
					if (this.EventDetails && this.EventDetails.eventID) {
						this.clickCircleEventID = this.EventDetails.eventID;
					}

					// If we initally set the map center location on alarm point,
					// however need to reload the radius and the content inside,
					// we reset position.
					if (this.markers && this.markers.length > 0) {
						this.mapCenter = this.markers[0].position;
					}

					this.loadRadius(this.mapCenter);
				}
			}
		});
	}

	private displayLayers(layers: MapLayerData[]) {
		const itemLayerIDs: string[] = [];

		layers.forEach(layer => {
			/* let isElevated = (layer.minElevation == null || layer.minElevation! <= this.elevation) &&
												                (layer.maxElevation == null || layer.maxElevation! >= this.elevation); */
			const elevation = this.elevation;

			let isElevated =
				(layer.minElevation == null || layer.minElevation! <= elevation) &&
				(layer.maxElevation == null || layer.maxElevation! >= elevation);

			if (isElevated) {
				itemLayerIDs.push(layer.mapLayerID.toString());

				if (this.layersAdded[layer.mapLayerID] == undefined) {
					this.layersAdded[layer.mapLayerID] = this.enhancedMapping.createOverlay(
						this.map!,
						layer.mapLayerID,
						layer.title,
						stringToBounds(layer.ne, layer.sw), // boundaries
						layer.rotation, // rotation
						axiosInstance.defaults.baseURL + "/MapLayers/Image/" + layer.mapLayerID.toString(), // imageUrl
						layer.minElevation, // minElevation,
						layer.maxElevation, // maxElevation
						this.layersVisible // , visible
					);
				}
			}
		});

		for (var layerId in this.layersAdded) {
			if (itemLayerIDs.indexOf(layerId) == -1) {
				let layer = this.layersAdded[layerId];

				layer.remove();
				delete this.layersAdded[layerId];
			}
		}
	}

	public displayItems(mapItems: MapItemData[]) {
		let mapItemIDs: string[] = [];
		let markerTypeVisibilityByID = new LayerTypeMap();

		this.mapItemTypes.forEach(markerType => {
			markerTypeVisibilityByID[markerType.mapLayerItemTypeID] = markerType.visible;
		});

		if (this.filterMapItemsByDeviceIds) {
			mapItems = mapItems.filter(mi => this.deviceIdsToFilterBy.includes(mi.objectID));
		}

		if (this.filterMapItemsByType) {
			mapItems = mapItems.filter(mi => mi.mapLayerItemTypeID === this.mapItemTypeIdToFilterBy);
		}

		mapItems.forEach(mapItem => {
			let mapLayerItemID = mapItem.mapLayerItemID;

			if (
				(mapItem.minElevation == null || mapItem.minElevation! <= this.elevation) &&
				(mapItem.maxElevation == null || mapItem.maxElevation! >= this.elevation)
			) {
				mapItemIDs.push(mapItem.mapLayerItemID.toString());

				if (this.itemsAdded[mapLayerItemID] == undefined) {
					let mapMarkerItem = this.enhancedMapping.createMarker(
						this.map!,
						mapItem.mapLayerItemID,
						null,
						mapItem.mapLayerItemTypeID,
						mapItem.objectID,
						mapItem.title,
						convertStringToLatLng(mapItem.latLong!),
						mapItem.regionPath,
						mapItem.minElevation,
						mapItem.maxElevation,
						this.layersVisible
					);

					this.$emit("map-item-created", mapMarkerItem);

					this.itemsAdded[mapLayerItemID] = mapMarkerItem;

					if (markerTypeVisibilityByID[mapItem.mapLayerItemTypeID] != null) {
						this.itemsAdded[mapLayerItemID].markerTypeVisible =	markerTypeVisibilityByID[mapItem.mapLayerItemTypeID];
					}

					if (mapItem.objectID != null) {
						if (this.markersByTypeAndObject[mapItem.mapLayerItemTypeID] == null) {
							this.markersByTypeAndObject[mapItem.mapLayerItemTypeID] = new ItemObjectMap();
						}

						if (!this.markersByTypeAndObject[mapItem.mapLayerItemTypeID][mapItem.objectID!]) {
							this.markersByTypeAndObject[mapItem.mapLayerItemTypeID][mapItem.objectID!] = [];
						}

						this.markersByTypeAndObject[mapItem.mapLayerItemTypeID][mapItem.objectID!].push(mapMarkerItem);

						if (this.isFirstTwoEnabled) {
							mapMarkerItem.isClickable = true;
							mapMarkerItem.click = () => {
								const position = convertStringToLatLng(mapItem!.latLong!);
								this.updatePointOfInterest(position, undefined);
							};
						}

						if (mapItem.mapLayerItemTypeID == 4) {
							const activeStatus = this.activeResponseIDs[mapItem.objectID!.toString()];
							if (!activeStatus) {
								mapMarkerItem.state = "";
							} else if (activeStatus && activeStatus.awaitingAcknowledgement) {
								mapMarkerItem.state = "active";
							} else {
								mapMarkerItem.state = "processing";
							}
						} else {
							if (mapItem.mapLayerItemTypeID == 1) {
								mapMarkerItem.isClickable = true;
								mapMarkerItem.click = () => this.onItemClick(mapItem);
							}

							if (this.activeItemIDs.has(mapLayerItemID)) {
								mapMarkerItem.state = "active";
							}
						}

						if (mapItem.brokenObjectLink) {
							let brokenItemID = -1;
							mapMarkerItem.state = "broken";
							this.itemsAdded[mapLayerItemID].markerTypeVisible =	markerTypeVisibilityByID[brokenItemID];

							if (this.markersByTypeAndObject[brokenItemID] == null) {
								this.markersByTypeAndObject[brokenItemID] = new ItemObjectMap();
							}

							if (!this.markersByTypeAndObject[brokenItemID][mapItem.objectID!]) {
								this.markersByTypeAndObject[brokenItemID][mapItem.objectID!] = [];
							}

							this.markersByTypeAndObject[brokenItemID][mapItem.objectID!].push(mapMarkerItem);
						}
					}
				}
			}
		});

		for (var itemId in this.itemsAdded) {
			if (mapItemIDs.indexOf(itemId) == -1) {
				var item = this.itemsAdded[itemId];

				item.state = item.state == "broken" ? "broken" : "";

				if (this.markersByTypeAndObject[item.mapLayerItemTypeID] != null) {
					if (this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!] != null) {
						delete this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!];
					}
				}

				item.remove();
				delete this.itemsAdded[itemId];
			}
		}
	}

	public onItemClick(mapItem: MapItemData): void {
		this.$emit("onCameraIconClick");
		this.setAwaitingCamera({
			objectID: mapItem.objectID,
			title: mapItem.title
		});

		if (this.isFirstTwoEnabled) {
			const position = convertStringToLatLng(mapItem!.latLong!);
			this.updatePointOfInterest(position, undefined);
		}
	}

	public loadRadius(position: any) {
		if (this.clickCircle) {
			this.clickCircle.remove();
			this.clickCircle = undefined;
		}

		this.clickCircle = this.enhancedMapping.createCircle(
			this.map!,
			"#0d87e9",
			0.8,
			2,
			"#0d87e9",
			0.05,
			position,
			this.nearbyCameraRadius,
			this.allowSpotlightScaling
		);

		this.clickCircle.editted = latLng => this.getItemsInCircle();
		this.getItemsInCircle();
	}

	private rad(x: number) {
		return (x * Math.PI) / 180;
	}

	public getDistance(p1: any, p2: any): number {
		let R = 6378137; // Earth's mean radius in meters

		let dLat = this.rad(p2.lat - p1.lat);
		let dLong = this.rad(p2.lng - p1.lng);
		let a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this.rad(p1.lat)) * Math.cos(this.rad(p2.lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
		let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		let d = R * c;

		return Math.round(d);
	}

	public getItemsInCircle() {
		if (this.showCircle === false) {
			return;
		}

		this.clearDeviceControllerCameras();
		const bounds = this.clickCircle.squareBounds;

		let center = this.clickCircle!.center;
		if (center) {
			this.setMapCircleCenter(center.lat + " " + center.lng);
		}

		if (bounds) {
			const requestBounds = {
				north: bounds.north,
				south: bounds.south,
				east: bounds.east,
				west: bounds.west
			} as MapBounds;

			if (this.elevation) {
				bounds.elevation = this.elevation;
			} else {
				// If we don't have any elevation at this point,
				// set elevation to be default: 0
				this.elevation = 0;
			}

			this.loadMapLayers(requestBounds).then((response: any) => {
				let items = response.data.items as MapItemData[];

				let newActiveItems = new Array<IMarker>();
				this.activeItemIDs = new Set<number>();

				var centerPosition = this.clickCircle!.center;
				var center = { lat: centerPosition.lat, lng: centerPosition.lng };
				var radius = this.clickCircle!.radius;
				let itemDistances = new Array<any>();

				let activeMapLayerItems = new Set();

				items.forEach(item => {
					if (item.mapLayerItemTypeID != 4) {
						let marker = this.itemsAdded[item.mapLayerItemID];
						let distance;

						if (marker != null) {
							distance = this.getDistance(center, {
								lat: marker.position.lat,
								lng: marker.position.lng
							});

							if (distance <= radius) {
								marker.state = marker.state == "broken" ? "broken" : "active";

								itemDistances.push({
									distance: distance,
									itemType: item.mapLayerItemTypeID,
									title: marker.title,
									objectID: marker.objectID
								});

								newActiveItems.push(marker);
								activeMapLayerItems.add(item.mapLayerItemID);
								this.activeItemIDs.add(item.mapLayerItemID);
							}
						} else if (item!.latLong != null) {
							const position = convertStringToLatLng(item!.latLong!);
							distance = this.getDistance(center, {
								lat: position.lat,
								lng: position.lng
							});

							if (distance <= radius && item.minElevation == this.elevation) {
								this.activeItemIDs.add(item.mapLayerItemID);
								activeMapLayerItems.add(item.mapLayerItemID);
								itemDistances.push({
									distance: distance,
									itemType: item.mapLayerItemTypeID,
									title: item.title,
									objectID: item.objectID,
									imperial: false
								});
							} else {
								return;
							}
						}
					}
				});

				if (this.activeItems != null) {
					this.activeItems.forEach(marker => {
						if (!activeMapLayerItems.has(marker.mapLayerItemID)) {
							if (marker.mapLayerItemTypeID != 4) {
								marker.state = marker.state == "broken" ? "broken" : "";
							}
						}
					});
				}
				this.activeItems = newActiveItems;

				itemDistances = itemDistances.sort((a, b) => {
					return a.distance - b.distance;
				});

				var uniqueDistances = [];
				itemDistances.forEach(distance => {
					if (
						!uniqueDistances.find(
							item => item.itemType == distance.itemType && item.objectID == distance.objectID
						)
					) {
						uniqueDistances.push(distance);
					}
				});
				itemDistances = uniqueDistances;

				if (this.getUseImperialMeasurements) {
					itemDistances.forEach(distance => {
						distance.distance = this.metersToFeet(distance.distance);
						distance.imperial = true;
					});
				}

				this.setActiveMapItems(itemDistances);
				this.setActiveMapItemsRequired(false);

				if (this.clickCircle && this.allowSpotlightScaling) this.clickCircle!.editable = true;

				// localStorage.setItem('activeMapItems', JSON.stringify(itemDistances));
			});
		}
	}

	private metersToFeet(meters) {
		if (meters < 0) return 0;
		else return Math.round(meters / 0.3048);
	}

	private createAdHocMarker(record: any) {
		var location = record.location.split(",");

		this.adHocAlarmMarkers[record.eventRecordID] = this.enhancedMapping.createMarker(
			this.map!,
			null,
			record.eventRecordID.toString(),
			4,
			record.objectID,
			record.details,
			convertStringToLatLng(location[0]),
			null,
			null,
			null,
			true
		);

		this.adHocAlarmMarkers[record.eventRecordID].state = this.adHocAlarmMarkers[record.eventRecordID].state == "broken" ? "broken" : 
			(record.awaitingAcknowledgement ? "active" : "processing");
		if (this.isFirstTwoEnabled) {
			this.adHocAlarmMarkers[record.eventRecordID].isClickable = true;
			this.adHocAlarmMarkers[record.eventRecordID].click = () => {
				const position = convertStringToLatLng(location[0]);
				this.updatePointOfInterest(position, undefined);
			};
		}
	}

	public clearMap() {
		for (var layerId in this.layersAdded) {
			let layer = this.layersAdded[layerId];

			layer.remove();
			delete this.layersAdded[layerId];
		}

		for (var itemId in this.itemsAdded) {
			let item = this.itemsAdded[itemId];

			item.state = item.state == "broken" ? "broken" : "";

			if (this.markersByTypeAndObject[item.mapLayerItemTypeID] != null) {
				if (this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!] != null) {
					delete this.markersByTypeAndObject[item.mapLayerItemTypeID][item.objectID!];
				}
			}

			item.remove();
			delete this.itemsAdded[itemId];
		}
	}

	public upLevel() {
		let higherFloors = this.elevationList.filter(elevation => elevation.elevationValue > this.elevation);
		if (higherFloors.length > 0) {
			this.elevation = higherFloors[0].elevationValue;
			this.updateElevation();
		}
	}

	public downLevel() {
		let lowerFloors = this.elevationList.filter(elevation => elevation.elevationValue < this.elevation);
		if (lowerFloors.length > 0) {
			this.elevation = lowerFloors[lowerFloors.length - 1].elevationValue;
			this.updateElevation();
		}
	}

	public gotoElevation(elevation: any) {
		this.elevation = elevation.elevationValue;
		this.displayElevation = elevation.name;
		this.updateElevation();
	}

	public updateElevation() {
		this.clearMap();

		this.loadInBounds(this.map!.mapBounds, this.map!.currentViewState == "narrow");
		this.getItemsInCircle();
		this.updateMarkerElevationVisibility();
	}

	private updateMarkerElevationVisibility() {
		this.markers.forEach((marker, i, arr) => {
			const inRange: boolean = marker.elevation.min >= this.elevation && marker.elevation.max <= this.elevation;
			arr[i].marker.setVisible(inRange);
		});
	}

	public toggleVisibility(mapItemType: any) {
		var newMapItem = {
			...mapItemType,
			visible: !mapItemType.visible
		};

		this.setMapLayersItemType({ id: mapItemType.mapLayerItemTypeID, data: newMapItem });

		if (this.markersByTypeAndObject[newMapItem.mapLayerItemTypeID] != null) {
			let ByObject = this.markersByTypeAndObject[newMapItem.mapLayerItemTypeID];

			
			for (let objectId in ByObject) {
				ByObject[objectId].forEach(marker => {
					let mapItemTypeID = newMapItem.mapLayerItemTypeID;
					// Only adjust visibility for items that broken when adjusting visibility on broken items filter
					// or any other item when NOT adjusting broken item filter
					if ((mapItemTypeID === -1 && marker.state === "broken") || (mapItemTypeID !== -1 && marker.state !== "broken")) {
						// if showInactiveAlarmsOnMap feature is on
						if (this.showInactiveAlarmsOnMap || this.showBrokenItemsFeatureEnabled) {
							// if the item is an alarm, and is NOT in an inactive state toggle its visibility (based on its assigned image)
							// inactive alarms are handled by toggleInactiveAlarmVisibility() separately
							if (mapItemTypeID == 4 && !marker._markerState.imageUrl.includes("alarm_inactive") ) {
								marker.markerTypeVisible = newMapItem.visible;
							}
							// if it is not an alarm
							else if (mapItemTypeID != 4) {
								if ((this.showBrokenItemsFeatureEnabled && marker.state === "broken") || marker.state !== "broken")
								marker.markerTypeVisible = newMapItem.visible;
							}
						}
						else {
							marker.markerTypeVisible = newMapItem.visible;
						}
					}
				});
			}
		}

		if (newMapItem.mapLayerItemTypeID == 4) {
			for (let eventRecordID in this.adHocAlarmMarkers) {
				this.adHocAlarmMarkers[eventRecordID].visible = newMapItem.visible;
			}
		}

		this.toggleMarkersVisibilitty(newMapItem.visible, newMapItem.mapLayerItemTypeID);
	}

	public async toggleExternalMapLayerVisibility(mapLayerItem: ExternalMapLayer) {
		let updatedMapLayer = cloneDeep(mapLayerItem);
		updatedMapLayer.displaying = !mapLayerItem.displaying;

		await this.updateExternalMapLayer(updatedMapLayer);
	}

	@Watch("externalLayersList")
	async onExternalMapLayersListChanged(list: ExternalMapLayer[]) {
		// send list of layers to mapping library, all logic and functions is handled in there
		if (this.enhancedMapping) {
			await this.enhancedMapping.loadExternalMapLayers(list, this.map.map);
		}
	}

	public destroyed() {
		this.clearMap();
	}

	private showInactiveAlarms: boolean = false;

	public toggleInactiveAlarmVisibility(mapItemType: any) {
		// if the feature is enabled
		if (this.showInactiveAlarmsOnMap) {
			// toggle the status of the filter
			this.showInactiveAlarms = !this.showInactiveAlarms;

			// get all marks that match this type
			if (this.markersByTypeAndObject[mapItemType.mapLayerItemTypeID]) {
				var ByObject = this.markersByTypeAndObject[mapItemType.mapLayerItemTypeID];

				//
				for (var objectId in ByObject) {
					ByObject[objectId].forEach(marker => {
						// toggle the visibility only if it is inactive state
						if (marker._markerState.imageUrl.includes("alarm_inactive")) {
							marker._markerState.hidden = !this.showInactiveAlarms;
							marker.markerTypeVisible = this.showInactiveAlarms;
						}
					});
				}
			}
		}
	}

	private get getInactiveAlarmIconPath() {
		return `${window.location.pathname}images/enhancedmapping/alarm_inactive.png`;
	}

	private setNewIconOnMapLayer(newMarker: NewMarker): void {
		this.newMarker = newMarker;

		if (this.map) {
			this.map!.setCursor("crosshair");
		}
	}

	private setIconOnMapLayer(marker: NewMarker): void {
		if (!marker) return;
		this.newMarker = marker;
		const latLng = convertStringToLatLng(this.newMarker.marker.latLong);
		this.setMarkerOnMap(latLng, false, false);
	}

	private setMarkerOnMap(latLng: ILatLng, cb: boolean = true, newMarker: boolean = true) {
		this.map!.setCursor("grab");
		if (!this.newMarker) return;
		if (!this.newMarker.marker.latLong && (!latLng.lat || !latLng.lng)) return;

		// We want to verify that our marker doesn't already exist.
		if (this.markers.find(x => x.objectID == this.newMarker.marker.eventRecordId)) return;

		const minElevation = newMarker ? this.elevation : this.newMarker.marker.minElevation ?? this.elevation;
		const maxElevation = newMarker ? this.elevation : this.newMarker.marker.maxElevation ?? this.elevation;

		let mapMarkerItem = this.enhancedMapping.createMarker(
			this.newMarker.marker.map ?? this.map!, // Enhanced Mapping
			this.newMarker.marker.mapLayerItemId, // MapLayer Item Id
			this.newMarker.marker.eventRecordId, // Event Record Id
			this.newMarker.marker.mapLayerItemTypeId, // Map Item Type Id
			this.newMarker.marker.objectId, // Object Id
			this.newMarker.marker.title, // Title
			latLng ?? convertStringToLatLng(this.newMarker.marker.latLong), // Position
			this.newMarker.marker.regionPath, // Region Path
			minElevation, // Min Elevation
			maxElevation, // Max Elevation
			this.newMarker.marker.visible // Visible
		);

		mapMarkerItem.state = this.newMarker.state;

		if (this.newMarker.setSpotlight) {
			this.mapCenter = latLng;
			this.loadRadius(latLng);
		}

		this.markers.push({
			...mapMarkerItem,
			elevation: {
				min: minElevation,
				max: maxElevation
			}
		});

		mapMarkerItem.marker.setVisible((minElevation >= this.elevation && maxElevation <= this.elevation) as boolean);

		// Emit up that we have completed our marker place.
		if (cb) {
			this.eventBus.$emit("set-current-marker", {
				marker: this.newMarker.marker,
				latLng: latLng,
				elevation: this.elevation
			});
		}

		this.newMarker = null;
	}

	private async getArcGisParameters(requestedLayer: ExternalMapLayer): Promise<object> {
		return await this.enhancedMapping.getArcGisParameters(requestedLayer);
	}

	// LAYER PROPERTY FILTERING
	@Getter getPermissions: UserPermissions;

	public selectedParameters: ExternalMapLayerParameter[] = null;
	public selectedTitle: string = "";
	public selectedLayer: ExternalMapLayer = null;
	private showLayerProps = false;

	private async openLayerProps(selectedLayer: ExternalMapLayer) {
		this.selectedLayer = selectedLayer;
		this.selectedParameters = selectedLayer.parameters;
		this.selectedTitle = selectedLayer.title;
		this.showLayerProps = true;
	}
	private async closeLayerProps() {
		this.showLayerProps = false;
	}
	private async applyMapLayerProps() {
		this.selectedLayer.parameters = this.selectedParameters;
		await this.updateExternalMapLayer(this.selectedLayer);

		this.showLayerProps = false;
	}

	public isExternalMapLayerPropertiesEnabled() {
		return get(this.featuresList, ["ExternalMapLayers", "AutoSyncAttributes"]);
	}

	private canViewProperties(): boolean {
		if (
			(this.getPermissions.canViewExternalMapLayers || this.getPermissions.canEditExternalMapLayers) &&
			this.isExternalMapLayerPropertiesEnabled()
		) {
			return true;
		}
		return false;
	}
}
